import React, { useState, useEffect, useMemo } from 'react';
import { api, uploadImage, API_BASE, getToken } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import Alert from '@mui/material/Alert';
import { IoPricetagsOutline, IoCreateOutline, IoEllipsisVerticalSharp, IoCloudUploadOutline, IoSyncOutline } from 'react-icons/io5';
import { MdSearch } from 'react-icons/md';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function AdminCategories() {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', sort_order: 0, parent_id: '', image_url: '', gender_scope: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCategoryId, setMenuCategoryId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [csvFile, setCsvFile] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvError, setCsvError] = useState('');
  const csvInputRef = React.useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api('/api/admin/categories').then((data) => { setList(data); setError(''); }),
      api('/api/admin/products').catch(() => []),
    ])
      .then(([_, productList]) => setProducts(Array.isArray(productList) ? productList : []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const productCountByCategory = useMemo(() => {
    const map = {};
    (products || []).forEach((p) => {
      const id = p.category_id;
      map[id] = (map[id] || 0) + 1;
    });
    return map;
  }, [products]);

  const idToCategory = useMemo(() => {
    const map = {};
    list.forEach((c) => { map[c.id] = c; });
    return map;
  }, [list]);

  const getCategoryPath = (c) => {
    const parts = [];
    let cur = c;
    while (cur) {
      parts.unshift(cur.name);
      cur = cur.parent_id != null ? idToCategory[cur.parent_id] : null;
    }
    return parts.join(' › ');
  };

  const getDescendantIds = (id) => {
    const ids = [];
    const collect = (pid) => {
      list.filter((c) => c.parent_id === pid).forEach((c) => {
        ids.push(c.id);
        collect(c.id);
      });
    };
    collect(id);
    return ids;
  };

  const parentOptions = useMemo(() => {
    const exclude = editing ? [editing.id, ...getDescendantIds(editing.id)] : [];
    const options = [{ id: '', label: '— None (top-level)' }];
    list.forEach((c) => {
      if (exclude.includes(c.id)) return;
      options.push({ id: c.id, label: getCategoryPath(c) });
    });
    return options;
  }, [list, editing]);

  const treeOrderedList = useMemo(() => {
    const byParent = {};
    list.forEach((c) => {
      const pid = c.parent_id != null ? c.parent_id : 0;
      if (!byParent[pid]) byParent[pid] = [];
      byParent[pid].push(c);
    });
    const sortCat = (a, b) => (a.sort_order - b.sort_order) || (a.name || '').localeCompare(b.name || '');
    [0, ...list.filter((c) => c.parent_id == null).map((c) => c.id)].forEach((pid) => {
      if (byParent[pid]) byParent[pid].sort(sortCat);
    });
    const out = [];
    const walk = (pid) => {
      (byParent[pid] || []).forEach((c) => {
        out.push(c);
        walk(c.id);
      });
    };
    walk(0);
    return out;
  }, [list]);

  const filteredList = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return treeOrderedList;
    return treeOrderedList.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.slug || '').toLowerCase().includes(q) || getCategoryPath(c).toLowerCase().includes(q));
  }, [treeOrderedList, search]);

  const paginatedList = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredList.slice(start, start + rowsPerPage);
  }, [filteredList, page, rowsPerPage]);

  const totalEntries = filteredList.length;

  const handleOpenDialog = (category = null) => {
    setEditing(category);
    setForm({
      name: category ? category.name : '',
      slug: category ? (category.slug || '') : '',
      sort_order: category != null ? (category.sort_order ?? 0) : 0,
      parent_id: category?.parent_id != null ? String(category.parent_id) : '',
      image_url: category?.image_url || '',
      gender_scope: category?.gender_scope || '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm({ name: '', slug: '', sort_order: 0, parent_id: '', image_url: '', gender_scope: '' });
  };

  const categoryImageInputRef = React.useRef(null);
  const handleUploadCategoryImage = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: url }));
    } catch (_) {}
    setUploadingImage(false);
    if (categoryImageInputRef.current) categoryImageInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        sort_order: Number(form.sort_order) || 0,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
        image_url: form.image_url.trim() || null,
        gender_scope: ['male', 'female', 'unisex'].includes(form.gender_scope) ? form.gender_scope : null,
      };
      if (editing) {
        await api(`/api/admin/categories/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api('/api/admin/categories', { method: 'POST', body: JSON.stringify(payload) });
      }
      handleCloseDialog();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (c) => {
    setAnchorEl(null);
    setMenuCategoryId(null);
    handleOpenDialog(c);
  };

  const handleDelete = async (id) => {
    setAnchorEl(null);
    setMenuCategoryId(null);
    if (!window.confirm('Remove this category? Products in it will be uncategorized.')) return;
    setError('');
    try {
      await api(`/api/admin/categories/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMenuOpen = (e, id) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setMenuCategoryId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCategoryId(null);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    const pageIds = paginatedList.map((c) => c.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} category(ies)? Products in them will be uncategorized. This cannot be undone.`)) return;
    setError('');
    try {
      await api('/api/admin/categories/bulk-delete', { method: 'POST', body: JSON.stringify({ ids: Array.from(selectedIds) }) });
      setSelectedIds(new Set());
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCategoryCsvImport = async () => {
    if (!csvFile) {
      setCsvError('Choose a CSV file.');
      return;
    }
    setCsvError('');
    setCsvResult(null);
    setCsvLoading(true);
    try {
      const form = new FormData();
      form.append('file', csvFile);
      const token = getToken();
      const res = await fetch(API_BASE + '/api/admin/sync/categories-csv', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const data = res.ok ? await res.json().catch(() => ({})) : null;
      if (!res.ok) throw new Error((data && data.error) ? data.error : res.statusText || 'Import failed');
      setCsvResult(data.summary || data);
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = '';
      load();
    } catch (err) {
      setCsvError(err.message || 'Import failed');
    } finally {
      setCsvLoading(false);
    }
  };

  const handleSyncProductsToCategories = async () => {
    setCsvError('');
    setCsvResult(null);
    setSyncLoading(true);
    try {
      const data = await api('/api/admin/categories/sync-products', { method: 'POST', body: JSON.stringify({}) });
      setCsvResult(data.summary || data);
      load();
    } catch (err) {
      setCsvError(err.message || 'Sync failed');
    } finally {
      setSyncLoading(false);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
      <CardHeader
        title="Category List"
        sx={{ pb: 0 }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {selectedIds.size > 0 && (
              <Button variant="outlined" color="error" onClick={handleBulkDelete} sx={{ textTransform: 'none' }}>
                Delete selected ({selectedIds.size})
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<IoCreateOutline size={18} />}
              onClick={() => handleOpenDialog()}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Add Category
            </Button>
          </Box>
        }
      />
      <CardContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Import &amp; sync
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            <strong>Import CSV</strong> — first row headers; required <strong>name</strong>. Optional: slug, sort_order,
            parent_id, parent_name, parent_slug, image_url. <strong>Sync products</strong> — point products at categories
            by matching the stored category name (run after changing categories or adding products).
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<IoSyncOutline size={18} />}
              disabled={csvLoading || syncLoading}
              onClick={handleSyncProductsToCategories}
              sx={{ textTransform: 'none' }}
            >
              {syncLoading ? 'Syncing…' : 'Sync products to categories'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<IoCloudUploadOutline size={18} />}
              disabled={csvLoading || syncLoading}
              sx={{ textTransform: 'none' }}
            >
              Choose file
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv,text/plain"
                hidden
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  setCsvFile(f || null);
                  setCsvError('');
                  setCsvResult(null);
                }}
              />
            </Button>
            {csvFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                {csvFile.name}
              </Typography>
            )}
            <Button
              variant="contained"
              size="small"
              disabled={csvLoading || syncLoading || !csvFile}
              onClick={handleCategoryCsvImport}
              sx={{ textTransform: 'none' }}
            >
              {csvLoading ? 'Importing…' : 'Import CSV'}
            </Button>
          </Box>
          {csvError && (
            <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setCsvError('')}>
              {csvError}
            </Alert>
          )}
          {csvResult && (
            <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setCsvResult(null)}>
              {(csvResult.created != null || csvResult.updated != null) && (
                <>
                  Created: {csvResult.created ?? 0}, updated: {csvResult.updated ?? 0}, skipped: {csvResult.skipped ?? 0}.
                </>
              )}
              {csvResult.productsRelinked != null && (
                <>
                  {' '}
                  Products matched to categories by name: {csvResult.productsRelinked}.
                </>
              )}
              {csvResult.errors && csvResult.errors.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
                  {csvResult.errors.slice(0, 12).map((msg, i) => (
                    <li key={i}><Typography variant="caption" component="span">{msg}</Typography></li>
                  ))}
                  {csvResult.errors.length > 12 && (
                    <li><Typography variant="caption">… and {csvResult.errors.length - 12} more</Typography></li>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            justifyContent: 'space-between',
          }}
        >
          <TextField
            size="small"
            placeholder="Search Category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch size={20} color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Rows per page:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 70 }}>
              <Select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                variant="outlined"
                sx={{ height: 36 }}
              >
                {ROWS_PER_PAGE_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {filteredList.length === 0 ? (
          <AdminEmptyState message={search ? 'No categories match your search.' : 'No categories. Click "Add Category" to create one.'} icon={IoPricetagsOutline} />
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell padding="checkbox" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      <Checkbox
                        indeterminate={selectedIds.size > 0 && paginatedList.some((c) => selectedIds.has(c.id)) && !paginatedList.every((c) => selectedIds.has(c.id))}
                        checked={paginatedList.length > 0 && paginatedList.every((c) => selectedIds.has(c.id))}
                        onChange={toggleSelectAllPage}
                        aria-label="Select all on page"
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>Categories</TableCell>
                    <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>Gender</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>Total Products</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>Total Earning</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedList.map((c) => (
                    <TableRow key={c.id} hover selected={selectedIds.has(c.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          aria-label={`Select ${c.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: c.parent_id != null ? 3 : 0 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: c.parent_id == null ? 'primary.main' : 'primary.light',
                              color: 'primary.contrastText',
                              fontSize: '1rem',
                            }}
                          >
                            {(c.name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {c.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {c.parent_id != null ? getCategoryPath(c) : (c.slug ? `/${c.slug}` : '—')}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {c.gender_scope ? (
                          <Chip
                            size="small"
                            label={c.gender_scope === 'female' ? 'Women' : c.gender_scope === 'male' ? 'Men' : 'Unisex'}
                            color={c.gender_scope === 'female' ? 'secondary' : c.gender_scope === 'male' ? 'primary' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">All</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {productCountByCategory[c.id] ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(c)}
                          aria-label="Edit"
                          sx={{ color: 'text.secondary' }}
                        >
                          <IoCreateOutline size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, c.id)}
                          aria-label="More"
                          sx={{ color: 'text.secondary' }}
                        >
                          <IoEllipsisVerticalSharp size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
                mt: 2,
                px: 0,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {totalEntries === 0 ? 0 : page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalEntries)} of {totalEntries} entries
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(0)} aria-label="First page">
                  &#x00AB;
                </IconButton>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                  &#x2039;
                </IconButton>
                <Typography variant="body2" sx={{ px: 1 }}>
                  {page + 1}
                </Typography>
                <IconButton
                  size="small"
                  disabled={page >= Math.ceil(totalEntries / rowsPerPage) - 1}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  &#x203A;
                </IconButton>
                <IconButton
                  size="small"
                  disabled={page >= Math.ceil(totalEntries / rowsPerPage) - 1}
                  onClick={() => setPage(Math.max(0, Math.ceil(totalEntries / rowsPerPage) - 1))}
                  aria-label="Last page"
                >
                  &#x00BB;
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const c = list.find((x) => x.id === menuCategoryId);
            if (c) handleEdit(c);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => menuCategoryId != null && handleDelete(menuCategoryId)}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
              <TextField
                size="small"
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                size="small"
                label="Slug (optional)"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="e.g. jackets"
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="Sort order"
                value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
                sx={{ width: 140 }}
              />
              <FormControl fullWidth size="small">
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Gender scope</Typography>
                <Select
                  value={form.gender_scope}
                  onChange={(e) => setForm((f) => ({ ...f, gender_scope: e.target.value }))}
                  displayEmpty
                >
                  <MenuItem value="">All (show in every view)</MenuItem>
                  <MenuItem value="female">Women only</MenuItem>
                  <MenuItem value="male">Men only</MenuItem>
                  <MenuItem value="unisex">Unisex (show in Men + Women)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Parent category</Typography>
                <Select
                  value={form.parent_id}
                  onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
                  displayEmpty
                >
                  {parentOptions.map((opt) => (
                    <MenuItem key={opt.id || 'none'} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Image (homepage category grid)</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Paste URL or upload"
                    value={form.image_url}
                    onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                  <Button component="label" variant="outlined" size="small" disabled={uploadingImage}>
                    {uploadingImage ? '…' : 'Upload'}
                    <input
                      ref={categoryImageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      hidden
                      onChange={handleUploadCategoryImage}
                    />
                  </Button>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
}
